/**
 * Modale plein-écran (mobile-first) pour SÉLECTIONNER le texte d'un indice.
 * Rend en MarkdownMath les blocs sources (statement + statement_question +
 * correction.answer_md des questions précédentes). Le tuteur surligne au doigt
 * ou au curseur → la sélection courante est capturée via window.getSelection()
 * puis validée. On avertit si le texte a plusieurs occurrences dans le scope.
 *
 * Mobile-first strict : bottom sheet full-height, actions primaires en bas
 * `w-full`, safe area iOS.
 */

import { useEffect, useMemo, useState } from 'react';
import { X, MousePointerClick } from 'lucide-react';
import { MarkdownMath, cn } from '@jeraisonne/ui';
import { verifyClueSourceText, type ClueHuntSourceCandidate } from '@jeraisonne/db';

export interface SelectionResult {
  source_text: string;
  /** Choisi manuellement si l'ambiguïté ne peut pas être levée. */
  source_block_id?: string;
}

export interface SelectClueSourceModalProps {
  sources: readonly ClueHuntSourceCandidate[];
  onClose: () => void;
  onSelected: (r: SelectionResult) => void;
}

export function SelectClueSourceModal({
  sources,
  onClose,
  onSelected,
}: SelectClueSourceModalProps) {
  const [selected, setSelected] = useState<string>('');
  const [scopeBlockId, setScopeBlockId] = useState<string | undefined>(undefined);

  const verdict = useMemo(
    () => verifyClueSourceText(selected, sources, scopeBlockId),
    [selected, sources, scopeBlockId],
  );

  // Capture la sélection à chaque changement de l'API browser.
  useEffect(() => {
    function handler() {
      const s = window.getSelection?.();
      const txt = s?.toString() ?? '';
      // On ne modifie que si le tuteur a réellement sélectionné qq chose non vide.
      if (txt.trim().length > 0) setSelected(txt);
    }
    document.addEventListener('selectionchange', handler);
    return () => document.removeEventListener('selectionchange', handler);
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const canValidate = selected.trim().length > 0 && verdict.kind !== 'none';

  function validate() {
    if (!canValidate) return;
    onSelected({
      source_text: selected.trim(),
      source_block_id: verdict.kind === 'ambiguous' && scopeBlockId ? scopeBlockId : undefined,
    });
    onClose();
  }

  return (
    <div
      className="fixed inset-0 z-[70] flex flex-col justify-end bg-black/50 backdrop-blur-sm sm:items-center sm:justify-center sm:p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Sélectionner un indice"
    >
      <div
        className="pb-safe flex max-h-[92vh] w-full flex-col overflow-hidden rounded-t-3xl bg-white shadow-2xl sm:max-w-2xl sm:rounded-3xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="border-ink-100 flex shrink-0 items-center justify-between border-b px-5 py-3.5">
          <h2 className="font-display text-ink-900 text-base font-bold">Sélectionner un indice</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            className="text-ink-400 hover:bg-ink-100 hover:text-ink-700 flex min-h-11 min-w-11 items-center justify-center rounded-xl transition-colors active:scale-[0.98]"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Contenu scrollable */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          <p className="text-ink-500 mb-3 flex items-center gap-1.5 text-xs">
            <MousePointerClick className="h-3.5 w-3.5" />
            Surligne le texte exact que l'élève devra cliquer pour trouver cet indice.
          </p>

          {sources.length === 0 ? (
            <p className="text-ink-500 text-sm">Aucun bloc source disponible.</p>
          ) : (
            <ul className="select-text space-y-3">
              {sources.map((s) => (
                <li
                  key={s.id}
                  className={cn(
                    'rounded-xl border p-3',
                    scopeBlockId === s.id
                      ? 'border-primary-400 bg-primary-50'
                      : 'border-ink-200 bg-white',
                  )}
                >
                  <div className="text-ink-500 mb-1.5 text-[11px] font-semibold uppercase tracking-wide">
                    {s.label}
                  </div>
                  <div className="prose prose-sm text-ink-900 max-w-none">
                    <MarkdownMath>{s.content_md}</MarkdownMath>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Zone verdict + action */}
        <div className="border-ink-100 bg-ink-50 space-y-2 border-t px-5 py-3">
          <div className="rounded-xl bg-white p-2.5 text-sm">
            <div className="text-ink-500 mb-1 text-[11px] font-medium">Sélection actuelle</div>
            <div className="text-ink-900 min-h-[1.25rem] break-words font-mono text-xs">
              {selected.trim() || <span className="text-ink-400">— rien de sélectionné —</span>}
            </div>
          </div>

          {selected.trim() && verdict.kind === 'none' && (
            <div className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">
              ⚠️ Ce texte n'apparaît dans aucun bloc source. Vérifie la sélection.
            </div>
          )}
          {verdict.kind === 'unique' && (
            <div className="rounded-lg bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
              ✅ 1 occurrence trouvée dans « {verdict.occurrence.block_label} ».
            </div>
          )}
          {verdict.kind === 'ambiguous' && (
            <div className="space-y-1.5 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900">
              <div>
                ⚠️ {verdict.occurrences.length} occurrences trouvées. Restreins à un bloc ou raffine
                :
              </div>
              <div className="flex flex-wrap gap-1.5">
                <button
                  type="button"
                  onClick={() => setScopeBlockId(undefined)}
                  className={cn(
                    'min-h-[2rem] rounded-full px-2.5 py-1 text-[11px]',
                    scopeBlockId === undefined
                      ? 'bg-amber-800 text-white'
                      : 'bg-white text-amber-900',
                  )}
                >
                  Aucun scope
                </button>
                {[...new Set(verdict.occurrences.map((o) => o.block_id))].map((id) => {
                  const src = sources.find((s) => s.id === id);
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setScopeBlockId(id)}
                      className={cn(
                        'min-h-[2rem] rounded-full px-2.5 py-1 text-[11px]',
                        scopeBlockId === id ? 'bg-amber-800 text-white' : 'bg-white text-amber-900',
                      )}
                    >
                      {src?.label ?? id.slice(0, 8)}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <button
            type="button"
            disabled={!canValidate}
            onClick={validate}
            className={cn(
              'min-h-[2.75rem] w-full rounded-xl px-4 py-2 text-sm font-semibold text-white transition-colors active:scale-[0.98]',
              canValidate ? 'bg-primary-600 hover:bg-primary-700' : 'bg-ink-300',
            )}
          >
            Valider la sélection
          </button>
        </div>
      </div>
    </div>
  );
}
